(() => {
  const instances = new WeakMap();
  const initialise = (root) => {
    if (root.dataset.initialised === 'true') return;
    const offers = Array.from(root.querySelectorAll('[data-offer]'));
    if (!offers.length) return;
    if (!offers.some((offer) => offer.checked)) offers[0].checked = true;
    root.dataset.initialised = 'true';
    const controller = new AbortController();
    const listen = (element, type, handler) => element?.addEventListener(type, handler, { signal: controller.signal });
    let countdownInterval;
    let operationId = 0;
    let activeRequestController;
    const cleanup = () => {
      clearInterval(countdownInterval);
      controller.abort();
      activeRequestController?.abort();
      operationId += 1;
      delete root.dataset.initialised;
      instances.delete(root);
    };
    instances.set(root, cleanup);
    const countdowns = root.querySelectorAll('[data-countdown]');
    if (countdowns.length) {
      const duration = Number(countdowns[0].dataset.duration);
      const seconds = Number.isFinite(duration) && duration > 0 ? Math.floor(duration) : 600;
      const deadline = Date.now() + seconds * 1000;
      const tick = () => {
        if (!root.isConnected) {
          cleanup();
          return;
        }
        const remaining = Math.max(0, Math.ceil((deadline - Date.now()) / 1000));
        const display = `${String(Math.floor(remaining / 60)).padStart(2, '0')}:${String(remaining % 60).padStart(2, '0')}`;
        countdowns.forEach((countdown) => { countdown.textContent = display; });
        if (remaining === 0) clearInterval(countdownInterval);
      };
      countdownInterval = setInterval(tick, 1000);
      tick();
    }
    const checkout = root.querySelector('[data-checkout]');
    const dialog = root.querySelector('[data-bundle-dialog]');
    const error = root.querySelector('[data-error]');
    const strings = JSON.parse(root.querySelector('[data-bundle-strings]').textContent);
    const preview = root.dataset.preview === 'true';
    const editCart = root.querySelector('[data-cart-edit]');
    const cartItems = root.querySelector('[data-cart-items]');
    const cartNote = root.querySelector('[data-cart-note]');
    const productIds = new Set(offers.map((offer) => offer.dataset.product).filter(Boolean));
    const variantIds = new Set(offers.map((offer) => offer.dataset.variant).filter(Boolean));
    const initialDisabled = new Map(offers.map((offer) => [offer, offer.disabled]));
    let busy = false;
    let busyOffer;
    let cartSnapshot;
    let cartReadState = preview ? 'preview' : 'loading';
    let refreshSequence = 0;
    const formatter = new Intl.NumberFormat(root.dataset.locale || 'de-DE', { style: 'currency', currency: root.dataset.currency || 'USD' });
    const money = (cents) => formatter.format(cents / 100);
    root.querySelectorAll('[data-money-cents]').forEach((element) => {
      const cents = Number(element.dataset.moneyCents);
      if (Number.isFinite(cents)) element.textContent = money(cents);
    });
    let previousFocus;
    const selected = () => offers.find((offer) => offer.checked) || offers[0];
    const values = () => {
      const offer = selected();
      const price = Number(offer.dataset.price);
      const compare = Number(offer.dataset.compare);
      return { offer, price, compare, total: price };
    };
    const setText = (selector, text) => {
      const element = root.querySelector(selector);
      if (element) element.textContent = text;
    };
    const variantId = (item) => String(item.variant_id || item.id);
    const belongsToFamily = (item) => productIds.has(String(item.product_id)) || variantIds.has(variantId(item));
    const isSelectedLine = (item, offer) => variantId(item) === offer.dataset.variant && !item.selling_plan_allocation;
    const isNormalised = (cart, offer) => {
      const family = cart.items.filter(belongsToFamily);
      return family.length === 1 && isSelectedLine(family[0], offer) && family[0].quantity === 1;
    };
    const linePrice = (item) => Number(item.final_line_price ?? item.line_price ?? 0);
    const renderOtherItems = (items) => {
      if (!cartItems) return;
      cartItems.replaceChildren();
      cartItems.hidden = !items.length;
      if (!items.length) return;
      const heading = document.createElement('p');
      heading.className = 'bundle-summary__other-heading';
      heading.textContent = strings.otherItems;
      cartItems.append(heading);
      items.forEach((item) => {
        const row = document.createElement('div');
        row.className = 'bundle-summary__other-item';
        const name = document.createElement('span');
        const amount = document.createElement('strong');
        name.textContent = `${item.quantity} × ${item.product_title || item.title}`;
        amount.textContent = money(linePrice(item));
        row.append(name, amount);
        cartItems.append(row);
      });
    };
    const update = () => {
      const state = values();
      const knownCart = cartReadState === 'ready' && cartSnapshot;
      const otherItems = knownCart ? cartSnapshot.items.filter((item) => !belongsToFamily(item)) : [];
      const verified = knownCart && isNormalised(cartSnapshot, state.offer);
      const selectedLine = verified ? cartSnapshot.items.find((item) => isSelectedLine(item, state.offer)) : null;
      const price = selectedLine ? linePrice(selectedLine) : state.price;
      const projectedTotal = price + otherItems.reduce((sum, item) => sum + linePrice(item), 0);
      const total = verified ? cartSnapshot.total_price : projectedTotal;
      const subtotal = verified ? (cartSnapshot.items_subtotal_price ?? total) : projectedTotal;
      const baseline = Math.max(state.compare, price) + otherItems.reduce((sum, item) => sum + Number(item.original_line_price ?? linePrice(item)), 0);
      const totalsKnown = preview || knownCart;
      offers.forEach((offer) => {
        offer.closest('.bundle-offer').classList.toggle('is-selected', offer.checked);
        offer.disabled = busy || initialDisabled.get(offer);
      });
      setText('[data-summary-units]', '1');
      setText('[data-summary-name]', state.offer.dataset.cartTitle || state.offer.dataset.title);
      const summaryImage = root.querySelector('[data-summary-image]');
      const offerImage = state.offer.closest('.bundle-offer').querySelector('.bundle-offer__picture img');
      if (summaryImage && offerImage) {
        summaryImage.src = offerImage.currentSrc || offerImage.src;
        summaryImage.alt = state.offer.dataset.cartTitle || state.offer.dataset.title;
      }
      setText('[data-summary-price]', money(price));
      setText('[data-summary-compare]', money(state.compare));
      const compareNode = root.querySelector('[data-summary-compare]');
      if (compareNode) compareNode.hidden = state.compare <= price;
      const discountNode = root.querySelector('[data-summary-discount]');
      if (discountNode) discountNode.hidden = state.compare <= price;
      const neutralNode = root.querySelector('[data-summary-neutral]');
      if (neutralNode) neutralNode.hidden = state.compare > price;
      setText('[data-subtotal]', totalsKnown ? money(subtotal) : '—');
      const savings = Math.max(0, baseline - total);
      setText('[data-savings]', totalsKnown ? `−${money(savings)}` : '—');
      const savingsRow = root.querySelector('[data-savings-row]');
      if (savingsRow) savingsRow.hidden = !totalsKnown || savings <= 0;
      setText('[data-total]', totalsKnown ? money(total) : '—');
      renderOtherItems(otherItems);
      if (cartNote) {
        cartNote.textContent = busy ? strings.preparingCart : cartReadState === 'loading' ? strings.checkingCart : cartReadState === 'error' ? strings.cartReadError : verified ? strings.verifiedCart : otherItems.length ? strings.estimatedTotal : strings.selectedOffer;
      }
      const unavailable = state.offer.dataset.available !== 'true' || !/^\d+$/.test(state.offer.dataset.variant || '');
      checkout.disabled = busy || (!preview && unavailable);
      checkout.textContent = busy ? strings.loading : strings.checkout;
      root.setAttribute('aria-busy', busy ? 'true' : 'false');
      if (editCart) {
        editCart.setAttribute('aria-disabled', busy ? 'true' : 'false');
        editCart.tabIndex = busy ? -1 : 0;
      }
    };
    const request = async (url, payload, signal) => {
      const requestController = new AbortController();
      const abort = () => requestController.abort();
      if (signal?.aborted) abort();
      else signal?.addEventListener('abort', abort, { once: true });
      let timedOut = false;
      const timeout = setTimeout(() => {
        timedOut = true;
        abort();
      }, 15000);
      try {
        const response = await fetch(url, {
          method: payload ? 'POST' : 'GET',
          credentials: 'same-origin',
          cache: 'no-store',
          signal: requestController.signal,
          headers: payload ? { 'Content-Type': 'application/json', Accept: 'application/json' } : { Accept: 'application/json' },
          ...(payload ? { body: JSON.stringify(payload) } : {})
        });
        const result = await response.json();
        if (!response.ok) throw new Error(typeof result.description === 'string' ? result.description : strings.cartError);
        return result;
      } catch (failure) {
        if (timedOut) throw new Error(strings.cartError);
        throw failure;
      } finally {
        clearTimeout(timeout);
        signal?.removeEventListener('abort', abort);
      }
    };
    const readCart = async (signal) => {
      const cart = await request(root.dataset.cartReadUrl, null, signal);
      if (!Array.isArray(cart.items) || !Number.isFinite(cart.total_price)) throw new Error(strings.cartReadError);
      return cart;
    };
    const refreshCart = async () => {
      if (preview || busy || controller.signal.aborted) return;
      const sequence = ++refreshSequence;
      cartReadState = 'loading';
      update();
      try {
        const cart = await readCart(controller.signal);
        if (sequence !== refreshSequence || busy || controller.signal.aborted) return;
        cartSnapshot = cart;
        cartReadState = 'ready';
      } catch (failure) {
        if (sequence !== refreshSequence || busy || controller.signal.aborted) return;
        cartSnapshot = undefined;
        cartReadState = 'error';
      }
      update();
    };
    const reconcileCart = async (offer, signal, assertCurrent) => {
      let cart = await readCart(signal);
      assertCurrent();
      let attemptedAdd = false;
      const mutate = async (url, payload, committed) => {
        let failure;
        try {
          await request(url, payload, signal);
        } catch (problem) {
          failure = problem;
        }
        assertCurrent();
        const nextCart = await readCart(signal);
        assertCurrent();
        if (failure && !committed(nextCart)) throw failure;
        return nextCart;
      };
      for (let attempt = 0; attempt < 20; attempt += 1) {
        assertCurrent();
        const family = cart.items.filter(belongsToFamily);
        const keep = family.find((item) => isSelectedLine(item, offer));
        if (!keep) {
          if (attemptedAdd) throw new Error(strings.cartChanged);
          attemptedAdd = true;
          cart = await mutate(root.dataset.cartAddUrl, { items: [{ id: offer.dataset.variant, quantity: 1 }] }, (next) => next.items.some((item) => isSelectedLine(item, offer)));
          continue;
        }
        const extra = family.find((item) => item !== keep);
        const quantityBefore = family.reduce((sum, item) => sum + item.quantity, 0);
        if (extra || keep.quantity !== 1) {
          const item = extra || keep;
          cart = await mutate(root.dataset.cartChangeUrl, { id: item.key, quantity: extra ? 0 : 1 }, (next) => next.items.filter(belongsToFamily).reduce((sum, line) => sum + line.quantity, 0) < quantityBefore);
          continue;
        }
        cart = await readCart(signal);
        assertCurrent();
        if (isNormalised(cart, offer)) return cart;
      }
      throw new Error(strings.cartChanged);
    };
    const closeDialog = () => {
      dialog.close();
      previousFocus?.focus();
    };
    const openDialog = (title, content) => {
      previousFocus = document.activeElement;
      root.querySelector('[data-dialog-heading]').textContent = title;
      const target = root.querySelector('[data-dialog-content]');
      target.replaceChildren(content);
      dialog.showModal();
      dialog.querySelector('[data-close-dialog]').focus();
    };
    const previewOrder = () => {
      const state = values();
      const content = document.createElement('div');
      const intro = document.createElement('p');
      intro.textContent = strings.previewMessage;
      content.append(intro);
      const line = (label, price) => {
        const row = document.createElement('div');
        row.className = 'bundle-dialog__line';
        const left = document.createElement('span');
        const right = document.createElement('span');
        left.textContent = label;
        right.textContent = money(price);
        row.append(left, right);
        content.append(row);
      };
      line(state.offer.dataset.cartTitle || state.offer.dataset.title, state.price);
      line(strings.total, state.total);
      openDialog(strings.previewTitle, content);
    };
    offers.forEach((offer) => listen(offer, 'change', () => {
      if (busy) offers.forEach((item) => { item.checked = item === busyOffer; });
      else error.hidden = true;
      update();
    }));
    listen(root.querySelector('[data-close-dialog]'), 'click', closeDialog);
    listen(dialog, 'click', (event) => {
      if (event.target !== dialog) return;
      const bounds = dialog.getBoundingClientRect();
      if (event.clientX < bounds.left || event.clientX > bounds.right || event.clientY < bounds.top || event.clientY > bounds.bottom) closeDialog();
    });
    listen(dialog, 'close', () => previousFocus?.focus());
    root.querySelectorAll('[data-policy]').forEach((button) => {
      listen(button, 'click', () => {
        const template = root.querySelector(`[data-policy-content="${button.dataset.policy}"]`);
        openDialog(button.textContent, template.content.cloneNode(true));
      });
    });
    const proceed = async (destination) => {
      if (busy) return;
      if (preview) {
        previewOrder();
        return;
      }
      const state = values();
      const validVariant = /^\d+$/.test(state.offer.dataset.variant || '');
      if (!validVariant || state.offer.dataset.available !== 'true') {
        if (destination === 'cart') {
          window.location.assign(root.dataset.cartUrl);
          return;
        }
        error.textContent = strings.unavailable;
        error.hidden = false;
        return;
      }
      const currentOperation = ++operationId;
      activeRequestController = new AbortController();
      const signal = activeRequestController.signal;
      const assertCurrent = () => {
        if (operationId !== currentOperation || signal.aborted || controller.signal.aborted) {
          const failure = new Error(strings.cartChanged);
          failure.name = 'AbortError';
          throw failure;
        }
      };
      busy = true;
      busyOffer = state.offer;
      refreshSequence += 1;
      error.hidden = true;
      update();
      try {
        const prepare = async () => {
          assertCurrent();
          const cart = await reconcileCart(state.offer, signal, assertCurrent);
          assertCurrent();
          return cart;
        };
        const cart = window.navigator?.locks?.request
          ? await window.navigator.locks.request('bundle-offers-cart', { signal }, prepare)
          : await prepare();
        assertCurrent();
        cartSnapshot = cart;
        cartReadState = 'ready';
        update();
        try {
          const returnPath = window.location.pathname + window.location.search;
          if (window.location.pathname.includes('/collections/')) window.sessionStorage.setItem('bundle-offers:return-url', returnPath);
        } catch (storageFailure) {}
        if (destination === 'cart') {
          window.location.assign(root.dataset.cartUrl);
          return;
        }
        const form = document.createElement('form');
        form.method = 'POST';
        form.action = root.dataset.cartUrl;
        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = 'checkout';
        input.value = '1';
        form.append(input);
        document.body.append(form);
        form.submit();
      } catch (failure) {
        if (operationId !== currentOperation || controller.signal.aborted) return;
        if (destination === 'cart') {
          window.location.assign(root.dataset.cartUrl);
          return;
        }
        busy = false;
        activeRequestController = undefined;
        error.textContent = failure.message || strings.cartError;
        error.hidden = false;
        cartReadState = 'error';
        cartSnapshot = undefined;
        update();
        await refreshCart();
      }
    };
    listen(checkout, 'click', (event) => {
      event.preventDefault();
      return proceed('checkout');
    });
    listen(editCart, 'click', (event) => {
      event.preventDefault();
      return proceed('cart');
    });
    listen(window, 'pageshow', (event) => {
      if (event.persisted) {
        operationId += 1;
        activeRequestController?.abort();
        activeRequestController = undefined;
        busy = false;
        error.hidden = true;
      }
      void refreshCart();
    });
    listen(window, 'focus', () => { void refreshCart(); });
    update();
    void refreshCart();
  };
  const mount = (target = document) => {
    if (target.matches?.('[data-bundle-test]')) initialise(target);
    target.querySelectorAll?.('[data-bundle-test]').forEach(initialise);
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => mount());
  else mount();
  document.addEventListener('shopify:section:load', (event) => mount(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    if (event.target.matches?.('[data-bundle-test]')) instances.get(event.target)?.();
    event.target.querySelectorAll?.('[data-bundle-test]').forEach((root) => instances.get(root)?.());
  });
})();
